import { type FC, useState } from "react";
import { View, Text, Icon, Checkbox } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { NotificationRowLayout } from "../NotificationRowLayout/NotificationRowLayout";
import {
  DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  type NotificationChannel,
  type NotificationCategory,
  type NotificationCategoryPreferences,
} from "../../../preferences";

export type {
  NotificationChannel,
  NotificationCategory,
  NotificationChannelPreferences,
  NotificationCategoryPreferences,
} from "../../../preferences";

const CHEVRON_SIZE = 20;

const CATEGORIES: readonly NotificationCategory[] = ["alarmEvent", "cameraConfigChange"];
const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  alarmEvent: "Alarm Events",
  cameraConfigChange: "Camera Config Changes",
};

export const CHANNELS: readonly NotificationChannel[] = ["email", "sms", "push"];

/**
 * Which channels a category actually offers a checkbox for — Alarm Events
 * offers every channel; Camera Config Changes offers Email and Push, since
 * its dispatcher sends no SMS.
 */
const CATEGORY_CHANNELS: Record<NotificationCategory, ReadonlySet<NotificationChannel>> = {
  alarmEvent: new Set(["email", "sms", "push"]),
  cameraConfigChange: new Set(["email", "push"]),
};
export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
};

/** Shared empty default, so an omitted `unavailableChannels`/`hiddenChannels` is a stable value. */
export const NO_UNAVAILABLE_CHANNELS: ReadonlySet<NotificationChannel> = new Set();

/**
 * Channels rendered as a column at all — a channel no category offers (see
 * `CATEGORY_CHANNELS`), or one the caller hides, drops out of the grid
 * entirely, caption and reserved cell space included, rather than leaving a
 * dead column with no checkbox ever appearing in it.
 */
export function visibleChannels(hiddenChannels: ReadonlySet<NotificationChannel>): readonly NotificationChannel[] {
  return CHANNELS.filter(
    (channel) =>
      !hiddenChannels.has(channel) && CATEGORIES.some((category) => CATEGORY_CHANNELS[category].has(channel)),
  );
}

export interface NotificationPreferenceGroupProps {
  /** e.g. "All Cameras", a site name, or an individual camera name. */
  readonly label: string;
  readonly preferences: NotificationCategoryPreferences;
  readonly onToggleChannel: (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) => void;
  /**
   * Marks cells already covered by a broader scope (e.g. this site's Email is
   * on because "All Cameras" Email is on) — rendered checked and disabled
   * regardless of `preferences`, since toggling it here wouldn't create a row
   * that changes anything the user could observe. Purely a display overlay:
   * no row exists (or would be written) for a cell that's only checked because
   * of this. Omit for a scope with nothing broader than it ("All Cameras"
   * itself) — defaults to nothing inherited.
   */
  readonly inherited?: NotificationCategoryPreferences | undefined;
  /**
   * Channels that cannot deliver anything at all right now, whatever the
   * scope — SMS with no phone number on file, or Push while this device
   * isn't registered to receive it. Their checkboxes are
   * disabled at whatever they are already set to, rather than hidden or
   * silently unchecked: the preference is still real and comes back the
   * moment delivery is possible again. Unlike `inherited`, this says nothing
   * about the stored value, so it never changes what renders as checked.
   */
  readonly unavailableChannels?: ReadonlySet<NotificationChannel> | undefined;
  /**
   * Channels left out of the grid altogether — e.g. Push where no device can
   * register for it. Unlike `unavailableChannels`, nothing renders for them.
   * Every row sharing a column layout must be given the same set.
   */
  readonly hiddenChannels?: ReadonlySet<NotificationChannel> | undefined;
  /** Starts expanded unless set to `false` — used by stories to pin a collapsed state; the real screen always starts expanded. Ignored when `collapsible` is `false`. */
  readonly defaultExpanded?: boolean;
  /** False for a nested, non-collapsible scope (an individual site/camera inside `NotificationScopeListGroup`) — renders as a plain, always-expanded block with no chevron or press-to-collapse behavior. */
  readonly collapsible?: boolean;
  readonly testID?: string;
}

/**
 * One notification scope — "All Cameras", a single site, or a single camera —
 * rendered as a header followed by the two notification categories (Alarm
 * Events, Camera Config Changes), each with its own independent channel
 * checkboxes — Alarm Events offers Email/SMS/Push, Camera Config Changes
 * Email/Push (see `CATEGORY_CHANNELS`).
 */
export const NotificationPreferenceGroup: FC<NotificationPreferenceGroupProps> = ({
  label,
  preferences,
  onToggleChannel,
  inherited = DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  unavailableChannels = NO_UNAVAILABLE_CHANNELS,
  hiddenChannels = NO_UNAVAILABLE_CHANNELS,
  defaultExpanded = true,
  collapsible = true,
  testID,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isExpanded = collapsible ? expanded : true;
  const columns = visibleChannels(hiddenChannels);

  const captionColumns =
    isExpanded ?
      columns.map((channel) => (
        <Text key={channel} textStyle="xxs/regular" color="textMuted">
          {CHANNEL_LABELS[channel]}
        </Text>
      ))
    : [];

  return (
    <View style={styles.container} testID={testID}>
      {
        collapsible ?
          <NotificationRowLayout
            icon={<Icon name={isExpanded ? "chevronDown" : "chevronRight"} size={CHEVRON_SIZE} />}
            label={label}
            labelTextStyle="sm/semibold"
            onPressLabel={() => setExpanded((prev) => !prev)}
            columns={captionColumns}
          />
          // Matches `rows.paddingLeft` exactly, rather than an icon spacer — a
          // camera/site name should line up with its own category rows below,
          // not with a collapsible header's icon+label (a different, deeper
          // nesting level). No captions here: the containing
          // `NotificationScopeListGroup`'s own accordion header already shows
          // them once for the whole list.
        : <Text textStyle="sm/semibold" style={styles.scopeLabel}>
            {label}
          </Text>

      }
      {isExpanded && (
        <View style={styles.rows}>
          {CATEGORIES.map((category) => (
            <NotificationRowLayout
              key={category}
              label={CATEGORY_LABELS[category]}
              labelTextStyle="xs/regular"
              labelColor="textMuted"
              columns={columns.map((channel) => {
                if (!CATEGORY_CHANNELS[category].has(channel)) return null;
                const isInherited = inherited[category][channel];
                return (
                  <Checkbox
                    key={channel}
                    checked={preferences[category][channel] || isInherited}
                    disabled={isInherited || unavailableChannels.has(channel)}
                    onChange={(enabled) => onToggleChannel(category, channel, enabled)}
                  />
                );
              })}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.md,
  },
  // Matches `rows.paddingLeft` — see the comment where this is used.
  scopeLabel: {
    paddingLeft: theme.spacing["6xl"],
  },
  rows: {
    gap: theme.spacing.sm,
    paddingLeft: theme.spacing["6xl"],
  },
}));
